<script setup>
import getUuid from 'widget/helpers/uuid';
import { ref, onMounted, onUnmounted, defineEmits, defineExpose } from 'vue';
import WaveSurfer from 'wavesurfer.js';
import RecordPlugin from 'wavesurfer.js/dist/plugins/record.js';
import { format, intervalToDuration } from 'date-fns';
import { convertAudio } from './utils/mp3ConversionUtils';

const props = defineProps({
  audioRecordFormat: {
    type: String,
    required: true,
  },
});

const emit = defineEmits([
  'recorderProgressChanged',
  'finishRecord',
  'recordError',
  'pause',
  'play',
]);

const waveformContainer = ref(null);
const wavesurfer = ref(null);
const record = ref(null);
const isRecording = ref(false);
const isPlaying = ref(false);
const hasRecording = ref(false);

const formatTimeProgress = time => {
  const duration = intervalToDuration({ start: 0, end: time });
  return format(
    new Date(0, 0, 0, 0, duration.minutes, duration.seconds),
    'mm:ss'
  );
};

const handleRecordEnd = async blob => {
  const audioUrl = URL.createObjectURL(blob);
  const audioBlob = await convertAudio(blob, props.audioRecordFormat);
  const fileName = `${getUuid()}.mp3`;
  const file = new File([audioBlob], fileName, {
    type: props.audioRecordFormat,
  });
  // Eltafouk: un-awaited by design (it only feeds the waveform preview),
  // but if the agent sends right after stopping, clearMessage() unmounts
  // this component and onUnmounted's destroy() aborts this in-flight
  // load — an unhandled "signal is aborted without reason" rejection
  // that paged as a frontend-error alert despite being harmless. Only
  // swallow that expected AbortError; a genuine decode/load failure
  // should still surface. See project_voice_note_empty_send_race.
  wavesurfer.value.load(audioUrl).catch(err => {
    if (err?.name !== 'AbortError') throw err;
  });
  emit('finishRecord', {
    name: file.name,
    type: file.type,
    size: file.size,
    file,
  });
  hasRecording.value = true;
  isRecording.value = false;
};

const initWaveSurfer = () => {
  wavesurfer.value = WaveSurfer.create({
    container: waveformContainer.value,
    waveColor: '#1F93FF',
    progressColor: '#6E6F73',
    height: 100,
    barWidth: 2,
    barGap: 1,
    barRadius: 2,
    plugins: [
      RecordPlugin.create({
        scrollingWaveform: true,
        renderRecordedAudio: false,
      }),
    ],
  });

  wavesurfer.value.on('pause', () => emit('pause'));
  wavesurfer.value.on('play', () => emit('play'));

  record.value = wavesurfer.value.plugins[0];

  wavesurfer.value.on('finish', () => {
    isPlaying.value = false;
  });

  record.value.on('record-end', async blob => {
    try {
      await handleRecordEnd(blob);
    } catch (error) {
      // Eltafouk: this handler used to have no catch at all, so a failed
      // conversion rejected into window.onunhandledrejection — the agent got
      // NO error and NO file: the recorder just sat there and the voice note
      // was gone (2026-09-03, Nada, conv 181763; she re-recorded 37s later).
      // Tell the reply box so it can say what happened.
      isRecording.value = false;
      hasRecording.value = false;
      emit('recordError', error);
    }
  });

  record.value.on('record-progress', time => {
    emit('recorderProgressChanged', formatTimeProgress(time));
  });
};

const stopRecording = () => {
  if (isRecording.value) {
    record.value.stopRecording();
    isRecording.value = false;
  }
};

const startRecording = () => {
  isRecording.value = true;
  // Eltafouk: startRecording() is async (it awaits getUserMedia) and its
  // rejection was never handled — a blocked microphone only ever surfaced as
  // "Error accessing the microphone: Permission denied" in a frontend-error
  // alert (fatma, 2026-09-03), never to the agent, who saw a recorder that
  // simply never recorded.
  Promise.resolve(record.value.startRecording()).catch(error => {
    isRecording.value = false;
    if (error) error.isMicrophoneError = true;
    emit('recordError', error);
  });
};

const playPause = () => {
  if (hasRecording.value) {
    wavesurfer.value.playPause();
    isPlaying.value = !isPlaying.value;
  }
};

onMounted(() => {
  initWaveSurfer();
  startRecording();
});

onUnmounted(() => {
  if (wavesurfer.value) {
    wavesurfer.value.destroy();
  }
});

defineExpose({ playPause, stopRecording, record });
</script>

<template>
  <div ref="waveformContainer" class="w-full p-1" />
</template>
